import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './ExplanationModal.css';

/**
 * Reusable info modal for all games - used both for the room-level "info popup" (a note or
 * sign's title + body, shown instantly) and PuzzleChrome's concept guide (title + body
 * fetched async, so `loading`/`error` cover that in-flight state).
 * @param {boolean} isOpen - Whether the modal is visible
 * @param {Function} onClose - Callback when closing the modal
 * @param {string} title - Title of the explanation
 * @param {string} explanation - Main explanation text
 * @param {boolean} loading - Whether `explanation` is still being fetched
 * @param {string} error - Error message if loading fails
 */
function ExplanationModal({
  isOpen,
  onClose,
  title = 'Explanation',
  explanation = '',
  loading = false,
  error = ''
}) {
  return (
    <AnimatePresence>
      {isOpen && (
        // The modal is nested INSIDE the overlay (not a sibling) and the overlay does the
        // centering via flexbox, not the modal's own CSS `transform`. The modal is a
        // motion.div that already animates `scale`/`y` - framer-motion writes its own
        // `transform` straight to the element's inline style for those, which silently wins
        // over (completely replaces) any `transform: translate(-50%,-50%)` in the
        // stylesheet. That's what let the modal render with its top-LEFT corner at the
        // viewport's center instead of actually centered - invisible on a wide desktop
        // window (half the viewport still exceeded the modal's own max-width, so it
        // happened to fit anyway) but on any narrower/mobile viewport the right half of the
        // modal rendered off-screen, which is what made it look "not styled for mobile".
        <motion.div
          className="explanation-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            className="explanation-modal"
            initial={{ scale: 0.8, opacity: 0, y: 50 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.8, opacity: 0, y: 50 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="explanation-header">
              <h2>{title}</h2>
              <button className="explanation-close-btn" onClick={onClose}>
                ✕
              </button>
            </div>

            <div className="explanation-content">
              {loading && (
                <div className="loading-spinner">
                  <div className="spinner" />
                  <p>Loading...</p>
                </div>
              )}
              {!loading && error && (
                <div className="error-message">
                  ⚠️ {error}
                </div>
              )}
              {!loading && !error && (
                explanation ? (
                  <div className="explanation-section">
                    <div className="explanation-text">{explanation}</div>
                  </div>
                ) : (
                  <div className="no-explanation">
                    Nothing to show here yet.
                  </div>
                )
              )}
            </div>

            <div className="explanation-footer">
              <button className="explanation-footer-btn" onClick={onClose}>
                Close
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default ExplanationModal;
